/**
 * <s-size-finder> — recommends a size from height, weight and fit preference.
 *
 * Estimates body chest from height + weight, maps it to the store's oversized size chart
 * (garment chest S 42 / M 44 / L 46 / XL 48 / XXL 50 in), nudges for tall shoppers and for fit
 * preference, then limits to the sizes this product actually has. "Select" checks the matching
 * size radio in the main product form (which triggers the normal variant change).
 *
 * Data attributes (all strings come from Liquid translations):
 *   data-sizes            JSON [{ "value": "M", "available": true }, ...] in product order
 *   data-chart            JSON { "S": 42, "M": 44, ... } garment chest in inches
 *   data-option-name      Size option name used by the product form radios (e.g. "Size")
 *   data-t-result         "We recommend {size}"
 *   data-t-reason         "For {height} and {weight} with a {fit} fit. {size} has a {chest} in chest."
 *   data-t-between        "Between sizes? Pick {next} for an even baggier look."
 *   data-t-sold-out       "{size} is sold out right now. {alt} is the closest available size."
 *   data-t-none           "Sorry, none of the sizes that would fit you are in stock right now."
 *   data-t-select         "Select {size}"
 *   data-t-invalid        Validation message
 * Remembers the last inputs in localStorage so other products open prefilled.
 */
const ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];
const ALIASES = {
  'extra small': 'XS', small: 'S', medium: 'M', large: 'L',
  'extra large': 'XL', 'x-large': 'XL', '2xl': 'XXL', 'xx-large': 'XXL', 'xxxl': '3XL',
};
const STORAGE_KEY = 'senpai-size-finder';

function normalize(value) {
  const v = String(value || '').trim().toLowerCase();
  return ALIASES[v] || v.toUpperCase();
}

function fill(template, values) {
  return String(template || '').replace(/\{(\w+)\}/g, (_, key) => (key in values ? values[key] : ''));
}

class SSizeFinder extends HTMLElement {
  #ac;
  #recommended = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.form = this.querySelector('[data-size-finder-form]');
    this.result = this.querySelector('[data-size-finder-result]');
    this.unit = this.querySelector('[data-unit]');

    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.#recommend();
    }, opts);
    this.unit?.addEventListener('change', () => this.#syncUnit(), opts);
    this.querySelector('[data-size-finder-select]')?.addEventListener('click', () => this.#select(), opts);

    this.#restore();
    this.#syncUnit();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #syncUnit() {
    const imperial = this.unit?.value === 'ft';
    this.querySelectorAll('[data-unit-cm]').forEach((el) => { el.hidden = imperial; });
    this.querySelectorAll('[data-unit-ft]').forEach((el) => { el.hidden = !imperial; });
  }

  #heightCm() {
    if (this.unit?.value === 'ft') {
      const ft = Number(this.form.elements.namedItem('height_ft')?.value);
      const inch = Number(this.form.elements.namedItem('height_in')?.value || 0);
      if (!ft) return NaN;
      return (ft * 12 + inch) * 2.54;
    }
    return Number(this.form.elements.namedItem('height_cm')?.value);
  }

  #recommend() {
    const heightCm = this.#heightCm();
    const weightKg = Number(this.form.elements.namedItem('weight_kg')?.value);
    const fitInput = this.form.querySelector('input[name="fit"]:checked');
    const fit = fitInput?.value || 'oversized';
    const output = this.querySelector('[data-size-finder-output]');
    const selectBtn = this.querySelector('[data-size-finder-select]');

    if (!(heightCm >= 120 && heightCm <= 230) || !(weightKg >= 30 && weightKg <= 200)) {
      this.result.hidden = false;
      output.textContent = this.dataset.tInvalid || '';
      selectBtn.hidden = true;
      this.#recommended = null;
      return;
    }

    this.#save();

    let sizes = [];
    let chart = {};
    try { sizes = JSON.parse(this.dataset.sizes || '[]'); } catch { sizes = []; }
    try { chart = JSON.parse(this.dataset.chart || '{}'); } catch { chart = {}; }

    // Rough body chest (inches) from height and weight.
    const chestIn = (0.48 * weightKg + 0.17 * heightCm + 30) / 2.54;
    let index;
    if (chestIn < 32.5) index = ORDER.indexOf('XS');
    else if (chestIn <= 34.75) index = ORDER.indexOf('S');
    else if (chestIn <= 36.75) index = ORDER.indexOf('M');
    else if (chestIn <= 38.75) index = ORDER.indexOf('L');
    else if (chestIn <= 40.75) index = ORDER.indexOf('XL');
    else index = ORDER.indexOf('XXL');

    // Tall shoppers need body length: at least L from 185 cm, XL from 190 cm.
    if (heightCm >= 190) index = Math.max(index, ORDER.indexOf('XL'));
    else if (heightCm >= 185) index = Math.max(index, ORDER.indexOf('L'));

    if (fit === 'snug') index -= 1;
    if (fit === 'baggy') index += 1;

    const boundaries = [32.5, 34.75, 36.75, 38.75, 40.75];
    const nearUpper = boundaries.some((b) => b - chestIn > 0 && b - chestIn < 0.6);

    // Map to this product's sizes.
    const productSizes = sizes.map((s) => ({ ...s, code: normalize(s.value), rank: ORDER.indexOf(normalize(s.value)) }))
      .filter((s) => s.rank !== -1)
      .sort((a, b) => a.rank - b.rank);
    if (!productSizes.length) return;

    const minRank = productSizes[0].rank;
    const maxRank = productSizes[productSizes.length - 1].rank;
    index = Math.min(Math.max(index, minRank), maxRank);

    let pick = productSizes.reduce((best, s) => (Math.abs(s.rank - index) < Math.abs(best.rank - index) ? s : best), productSizes[0]);
    let note = '';

    if (!pick.available) {
      const alternatives = productSizes.filter((s) => s.available && Math.abs(s.rank - pick.rank) === 1);
      const alt = alternatives.find((s) => s.rank > pick.rank) || alternatives[0];
      if (alt) {
        note = fill(this.dataset.tSoldOut, { size: pick.value, alt: alt.value });
        pick = alt;
      } else {
        this.result.hidden = false;
        output.textContent = fill(this.dataset.tNone, {});
        selectBtn.hidden = true;
        this.#recommended = null;
        return;
      }
    } else if (nearUpper && fit !== 'baggy') {
      const next = productSizes.find((s) => s.rank === pick.rank + 1 && s.available);
      if (next) note = fill(this.dataset.tBetween, { next: next.value });
    }

    const heightLabel = this.unit?.value === 'ft'
      ? `${this.form.elements.namedItem('height_ft').value}'${this.form.elements.namedItem('height_in').value || 0}"`
      : `${Math.round(heightCm)} cm`;
    const fitLabel = fitInput?.dataset.label || fit;
    const chest = chart[pick.code];

    this.#recommended = pick.value;
    this.result.hidden = false;
    output.replaceChildren();

    const title = document.createElement('p');
    title.className = 'type-heading-sm';
    title.textContent = fill(this.dataset.tResult, { size: pick.value });
    const reason = document.createElement('p');
    reason.className = 'mt-1 text-sm text-on-surface-secondary';
    reason.textContent = fill(chest ? this.dataset.tReason : this.dataset.tReasonShort, {
      height: heightLabel, weight: `${weightKg} kg`, fit: fitLabel, size: pick.value, chest,
    });
    output.append(title, reason);
    if (note) {
      const extra = document.createElement('p');
      extra.className = 'mt-2 text-sm font-bold';
      extra.textContent = note;
      output.append(extra);
    }

    selectBtn.hidden = false;
    selectBtn.textContent = fill(this.dataset.tSelect, { size: pick.value });
  }

  #select() {
    if (!this.#recommended) return;
    const productForm = document.querySelector('[data-product-form]');
    const radios = productForm?.querySelectorAll(`input[type="radio"][name="${CSS.escape(this.dataset.optionName || '')}"]`) || [];
    const radio = [...radios].find((r) => r.value === this.#recommended);
    if (radio && !radio.disabled) {
      radio.click();
    } else {
      const select = productForm?.querySelector(`select[name="${CSS.escape(this.dataset.optionName || '')}"]`);
      if (select) {
        select.value = this.#recommended;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    this.closest('dialog')?.close();
  }

  #save() {
    try {
      const data = {
        unit: this.unit?.value,
        height_cm: this.form.elements.namedItem('height_cm')?.value,
        height_ft: this.form.elements.namedItem('height_ft')?.value,
        height_in: this.form.elements.namedItem('height_in')?.value,
        weight_kg: this.form.elements.namedItem('weight_kg')?.value,
        fit: this.form.querySelector('input[name="fit"]:checked')?.value,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch { /* storage unavailable */ }
  }

  #restore() {
    let data;
    try { data = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch { data = null; }
    if (!data || !this.form) return;
    if (data.unit && this.unit) this.unit.value = data.unit;
    ['height_cm', 'height_ft', 'height_in', 'weight_kg'].forEach((name) => {
      const el = this.form.elements.namedItem(name);
      if (el && data[name]) el.value = data[name];
    });
    if (data.fit) {
      const fitEl = this.form.querySelector(`input[name="fit"][value="${CSS.escape(data.fit)}"]`);
      if (fitEl) fitEl.checked = true;
    }
  }
}

if (!customElements.get('s-size-finder')) customElements.define('s-size-finder', SSizeFinder);
